import React, { useState, useEffect } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Phone, Mail, User, Package, Clock, CheckCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminLeads = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchLeads();
  }, [filter]);

  const fetchLeads = async () => {
    try {
      const statusQuery = filter !== 'all' ? `?status=${filter}` : '';
      const response = await fetch(`${BACKEND_URL}/api/leads${statusQuery}`);
      const data = await response.json();
      setLeads(data);
    } catch (error) {
      console.error('Error fetching leads:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateLeadStatus = async (leadId, newStatus) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/leads/${leadId}/status?status=${newStatus}`, {
        method: 'PATCH',
      });
      if (response.ok) {
        fetchLeads(); // Refresh the list
      }
    } catch (error) {
      console.error('Error updating lead status:', error);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'new': return 'bg-blue-100 text-blue-800';
      case 'contacted': return 'bg-yellow-100 text-yellow-800';
      case 'completed': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return <div className="container mx-auto px-4 py-8">Loading leads...</div>;
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Customer Leads</h1>
        <p className="text-gray-600">Manage your website inquiries</p>
      </div>

      {/* Filter Buttons */}
      <div className="flex gap-3 mb-6">
        <Button
          onClick={() => setFilter('all')}
          variant={filter === 'all' ? 'default' : 'outline'}
          className={filter === 'all' ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
        >
          All ({leads.length})
        </Button>
        <Button
          onClick={() => setFilter('new')}
          variant={filter === 'new' ? 'default' : 'outline'}
          className={filter === 'new' ? 'bg-blue-600 hover:bg-blue-700' : ''}
        >
          New
        </Button>
        <Button
          onClick={() => setFilter('contacted')}
          variant={filter === 'contacted' ? 'default' : 'outline'}
          className={filter === 'contacted' ? 'bg-yellow-600 hover:bg-yellow-700' : ''}
        >
          Contacted
        </Button>
        <Button
          onClick={() => setFilter('completed')}
          variant={filter === 'completed' ? 'default' : 'outline'}
          className={filter === 'completed' ? 'bg-green-600 hover:bg-green-700' : ''}
        >
          Completed
        </Button>
      </div>

      {/* Leads Grid */}
      {leads.length === 0 ? (
        <Card className="p-12 text-center">
          <p className="text-gray-500 text-lg">No leads yet. Waiting for customer inquiries...</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {leads.map((lead) => (
            <Card key={lead.id} className="p-6 border-2 hover:border-emerald-200 transition-all">
              {/* Status Badge */}
              <div className="flex justify-between items-start mb-4">
                <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(lead.status)}`}>
                  {lead.status.toUpperCase()}
                </span>
                <span className="text-sm text-gray-500 flex items-center">
                  <Clock className="w-4 h-4 mr-1" />
                  {formatDate(lead.created_at)}
                </span>
              </div>

              {/* Lead Info */}
              <div className="space-y-3 mb-4">
                <div className="flex items-center space-x-2">
                  <User className="w-5 h-5 text-emerald-600" />
                  <span className="font-semibold text-gray-900">{lead.name}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Phone className="w-5 h-5 text-emerald-600" />
                  <a href={`tel:${lead.phone}`} className="text-emerald-600 hover:underline">
                    {lead.phone}
                  </a>
                </div>
                {lead.email && (
                  <div className="flex items-center space-x-2">
                    <Mail className="w-5 h-5 text-emerald-600" />
                    <a href={`mailto:${lead.email}`} className="text-emerald-600 hover:underline">
                      {lead.email}
                    </a>
                  </div>
                )}
                <div className="flex items-center space-x-2">
                  <Package className="w-5 h-5 text-emerald-600" />
                  <span className="text-gray-700">{lead.service}</span>
                </div>
              </div>

              {/* Message */}
              {lead.message && (
                <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-700 italic">"{lead.message}"</p>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-2">
                {lead.status === 'new' && (
                  <Button
                    onClick={() => updateLeadStatus(lead.id, 'contacted')}
                    size="sm"
                    className="bg-yellow-600 hover:bg-yellow-700 text-white"
                  >
                    Mark as Contacted
                  </Button>
                )}
                {lead.status === 'contacted' && (
                  <Button
                    onClick={() => updateLeadStatus(lead.id, 'completed')}
                    size="sm"
                    className="bg-green-600 hover:bg-green-700 text-white"
                  >
                    <CheckCircle className="w-4 h-4 mr-1" />
                    Mark as Completed
                  </Button>
                )}
                {lead.status === 'completed' && (
                  <Button
                    onClick={() => updateLeadStatus(lead.id, 'contacted')}
                    size="sm"
                    variant="outline"
                  >
                    Reopen
                  </Button>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminLeads;
